import { CalendarIcon, ClockIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import dayjs from 'dayjs';
import dayjsTimezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

dayjs.extend(utc);
dayjs.extend(dayjsTimezone);

function toTz(ms: number, tz?: string) {
  return tz ? dayjs(ms).tz(tz) : dayjs(ms);
}

export interface EditableTimestampHandle {
  getTimestamp: () => number;
}

interface Props {
  timezone?: string;
  live?: boolean;
  readOnly?: boolean;
  containerClassName?: string;
  onFocus?: () => void;
  initialTimestamp?: number;
  onDatePickerChange?: (isoString: string) => void;
}

export const TIMESTAMP_PILL_SHAPE =
  'flex h-7 items-center gap-1 rounded-full px-1.5';

const DEFAULT_CONTAINER = `bg-white dark:bg-ondark-bg-3 ${TIMESTAMP_PILL_SHAPE}`;

export const EditableTimestamp = forwardRef<EditableTimestampHandle, Props>(
  function EditableTimestamp(
    {
      timezone,
      live = true,
      readOnly = false,
      containerClassName,
      onFocus,
      initialTimestamp,
      onDatePickerChange,
    },
    ref
  ) {
    const [tsEpoch, setTsEpoch] = useState(initialTimestamp ?? Date.now());
    const [edited, setEdited] = useState(!live);
    const [interacted, setInteracted] = useState(!live);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pickerRef = useRef<HTMLInputElement>(null);

    const [pickerValue, setPickerValue] = useState(() =>
      toTz(tsEpoch, timezone).format('YYYY-MM-DDTHH:mm:ss')
    );

    useEffect(() => {
      if (!live || edited || readOnly) return;
      const tick = () => setTsEpoch(toTz(Date.now(), timezone).valueOf());
      tick();
      timerRef.current = setInterval(tick, 1000);
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }, [live, edited, readOnly, timezone]);

    useImperativeHandle(ref, () => ({
      getTimestamp: () => tsEpoch,
    }));

    const openPicker = () => {
      if (readOnly) return;
      if (timerRef.current) clearInterval(timerRef.current);
      setInteracted(true);
      setPickerValue(toTz(tsEpoch, timezone).format('YYYY-MM-DDTHH:mm:ss'));
      onFocus?.();
      requestAnimationFrame(() => {
        const input = pickerRef.current;
        if (!input) return;
        if (typeof input.showPicker === 'function') {
          try {
            input.showPicker();
          } catch {
            input.focus();
          }
        } else {
          input.focus();
        }
      });
    };

    const handlePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setPickerValue(val);
      if (!val) return;
      const d = timezone ? dayjs.tz(val, timezone) : dayjs(val);
      if (!d.isValid()) return;
      setTsEpoch(d.valueOf());
      setEdited(true);
      onDatePickerChange?.(d.toISOString());
    };

    const container = containerClassName ?? DEFAULT_CONTAINER;
    const current = toTz(tsEpoch, timezone);
    const isToday = current.isSame(toTz(Date.now(), timezone), 'day');
    const isLiveTicking = live && !edited;
    const displayText = isToday
      ? current.format('HH:mm:ss')
      : current.format('DD-MM-YYYY, HH:mm:ss');

    return (
      <div className={clsx('relative', container)} onClick={openPicker}>
        <ClockIcon className="text-ondark-text-2 h-3.5 w-3.5 shrink-0" />
        <span
          className={clsx(
            'text-ondark-text-3 dark:text-onlight-line-3 cursor-pointer font-mono text-xs',
            isLiveTicking && 'w-16'
          )}
        >
          {displayText}
        </span>
        {!readOnly && interacted && (
          <CalendarIcon className="text-ondark-text-2 ml-2 h-3.5 w-3.5 cursor-pointer" />
        )}
        {!readOnly && (
          <input
            ref={pickerRef}
            type="datetime-local"
            step={1}
            value={pickerValue}
            onChange={handlePickerChange}
            onKeyDown={(e) => {
              if (e.key !== 'Tab') e.preventDefault();
            }}
            tabIndex={-1}
            className="pointer-events-none absolute inset-0 h-full w-full border-0 p-0 opacity-0"
          />
        )}
      </div>
    );
  }
);
